import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Inbox, Mail, Phone, CalendarDays, CalendarCheck, Package, Trash2, Glasses, Pencil, Tag, ClipboardList, TrendingUp, CheckCircle2, Download, ImagePlus, LayoutDashboard, Sparkles, AlertTriangle, Users, Menu, X, Copy, ScanFace, MessageCircle, ShoppingBag, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import ContenutiPanel from "@/components/admin/ContenutiPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Qualcosa è andato storto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const LENS_COLORS = [
  "Nero", "Marrone", "Marrone sfumato", "Avana / Tartaruga", "Verde G15",
  "Grigio sfumato", "Fumo", "Blu", "Specchiato argento", "Specchiato blu",
  "Specchiato oro", "Rosa", "Gialla", "Fotocromatica", "Polarizzata",
];

// Dominio pubblico di produzione: i link condivisibili puntano sempre al sito live
const BOOKING_BASE = "https://stylotticaceglie.com";
const BOOKING_LINKS = [
  { key: "generico", label: "Prenotazione (generico)", url: `${BOOKING_BASE}/prenota` },
  { key: "vista", label: "Occhiali da Vista", url: `${BOOKING_BASE}/prenota?tipo=vista` },
  { key: "lenti", label: "Lenti a Contatto", url: `${BOOKING_BASE}/prenota?tipo=lenti` },
];


const fmtDate = (d) =>
  new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState("panoramica");
  const [menuOpen, setMenuOpen] = useState(false);
  const [genDesc, setGenDesc] = useState(false);
  const [products, setProducts] = useState([]);
  const [pForm, setPForm] = useState({ nome: "", prezzo: "", categoria: "", sottocategoria: "", descrizione: "", stock: "", tryon: false, azione: "whatsapp", disponibile: true, in_evidenza: false, colore_lenti: "", taglia: "", misure: "" });
  const [pFile, setPFile] = useState(null);
  const [pExtraFiles, setPExtraFiles] = useState([]);
  const [pKeepImages, setPKeepImages] = useState([]);
  const [pLoading, setPLoading] = useState(false);
  const fileRef = useRef(null);
  const extraFileRef = useRef(null);
  const collFileRef = useRef(null);
  const [collImgTarget, setCollImgTarget] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [apptView, setApptView] = useState("tabella");
  const [showApptForm, setShowApptForm] = useState(false);
  const [savingAppt, setSavingAppt] = useState(false);
  const emptyApptForm = { nome: "", telefono: "", servizio: "", ambito: "vista", data: "", orario: "", note: "" };
  const [apptForm, setApptForm] = useState(emptyApptForm);
  const [apptSearch, setApptSearch] = useState("");
  const [apptStatus, setApptStatus] = useState("tutti");
  const [apptReparto, setApptReparto] = useState("tutti");
  const [apptPeriod, setApptPeriod] = useState("tutti");
  const [promos, setPromos] = useState([]);
  const [promoForm, setPromoForm] = useState({ titolo: "", tipo: "percentuale", percentuale: "", valore: "", prezzo_originale: "", testo_offerta: "", descrizione: "", valida_da: "", valida_fino: "" });
  const [promoLoading, setPromoLoading] = useState(false);
  const [collections, setCollections] = useState([]);
  const [subInputs, setSubInputs] = useState({});
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [orders, setOrders] = useState([]);
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [appointments, setAppointments] = useState([]);
  const [dash, setDash] = useState(null);
  const [pRetouched, setPRetouched] = useState(null);
  const [retouchingP, setRetouchingP] = useState(false);
  const [pExtraRetouched, setPExtraRetouched] = useState([]);
  const [retouchingExtra, setRetouchingExtra] = useState(false);
  const [pBgStyle, setPBgStyle] = useState("caldo");
  const [pTexture, setPTexture] = useState("nessuna");
  const [pPhotos, setPPhotos] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);

  const eur = (n) => n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/messages`, { withCredentials: true });
        setMessages(r.data);
      } catch (e) {
        if (e.response?.status === 401) {
          try {
            await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
            const r = await axios.get(`${API}/admin/messages`, { withCredentials: true });
            setMessages(r.data);
          } catch {
            setUser(false);
          }
        }
      }
    };
    load();
  }, [user]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const r = await axios.get(`${API}/products`, { withCredentials: true });
      setProducts(r.data);
    } catch { /* lista vuota */ }
  };

  const loadPromos = async () => {
    try {
      const r = await axios.get(`${API}/admin/promos`, { withCredentials: true });
      setPromos(r.data);
    } catch { /* lista vuota */ }
  };

  const loadCollections = async () => {
    try {
      const r = await axios.get(`${API}/collections`);
      setCollections(r.data);
    } catch { /* lista vuota */ }
  };

  const loadOrders = async () => {
    try {
      const r = await axios.get(`${API}/admin/orders`, { withCredentials: true });
      setOrders(r.data);
    } catch { /* lista vuota */ }
  };

  const onToggleOrder = async (id) => {
    await axios.put(`${API}/admin/orders/${id}/toggle`, {}, { withCredentials: true });
    loadOrders();
  };

  const pendingOrders = orders.filter((o) => o.status !== "completato").length;

  const loadAppointments = async () => {
    try {
      const r = await axios.get(`${API}/admin/appointments`, { withCredentials: true });
      setAppointments(r.data);
    } catch { /* lista vuota */ }
  };

  const onCreateAppointment = async (e) => {
    e.preventDefault();
    if (!apptForm.nome.trim() || !apptForm.data || !apptForm.orario) {
      toast.error("Nome, data e orario sono obbligatori.");
      return;
    }
    setSavingAppt(true);
    try {
      await axios.post(`${API}/admin/appointments`, apptForm, { withCredentials: true });
      toast.success("Appuntamento aggiunto!");
      setApptForm(emptyApptForm);
      setShowApptForm(false);
      loadAppointments();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Impossibile aggiungere l'appuntamento.");
    } finally {
      setSavingAppt(false);
    }
  };

  const onToggleAppointment = async (id) => {
    await axios.put(`${API}/admin/appointments/${id}/toggle`, {}, { withCredentials: true });
    loadAppointments();
  };

  const onDeleteAppointment = async (id) => {
    await axios.delete(`${API}/admin/appointments/${id}`, { withCredentials: true });
    loadAppointments();
  };

  const onDeleteOrder = async (id) => {
    await axios.delete(`${API}/admin/orders/${id}`, { withCredentials: true });
    loadOrders();
    toast.success("Ordine eliminato e giacenza ripristinata.");
  };

  const loadDash = async () => {
    try {
      const r = await axios.get(`${API}/admin/dashboard`, { withCredentials: true });
      setDash(r.data);
    } catch {
      setDash("errore");
    }
  };

  const onRetouchProduct = async () => {
    let file = pFile;
    if (!file && editingId) {
      const coverPath = pPhotos[0] || products.find((p) => p.id === editingId)?.image_path;
      if (!coverPath) return;
      const blob = await fetch(`${API}/files/${coverPath}`).then((r) => r.blob());
      file = new File([blob], "foto.webp", { type: blob.type || "image/webp" });
    }
    if (!file) {
      toast.error("Seleziona prima una foto della montatura.");
      return;
    }
    setRetouchingP(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("bg_style", pBgStyle);
      fd.append("texture", pTexture);
      const r = await axios.post(`${API}/admin/ai-retouch`, fd, { withCredentials: true, timeout: 120000 });
      if (editingId) {
        setPPhotos((prev) => (prev.length ? [r.data.path, ...prev.slice(1)] : [r.data.path]));
        setPRetouched(null);
      } else {
        setPRetouched(r.data.path);
      }
      setPFile(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Foto di copertina ritoccata! Controlla l'anteprima e salva il prodotto.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ritocco AI non riuscito. Riprova.");
    } finally {
      setRetouchingP(false);
    }
  };

  const onRetouchExtra = async () => {
    if (!pExtraFiles.length) {
      toast.error("Seleziona prima una o più foto aggiuntive.");
      return;
    }
    setRetouchingExtra(true);
    try {
      const done = [];
      for (const f of pExtraFiles) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("bg_style", pBgStyle);
        fd.append("texture", pTexture);
        const r = await axios.post(`${API}/admin/ai-retouch`, fd, { withCredentials: true, timeout: 120000 });
        done.push(r.data.path);
      }
      setPExtraRetouched((prev) => [...prev, ...done]);
      setPExtraFiles([]);
      if (extraFileRef.current) extraFileRef.current.value = "";
      toast.success(`${done.length} foto aggiuntiv${done.length === 1 ? "a ritoccata" : "e ritoccate"}! La copertina resta invariata. Ricorda di salvare.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ritocco AI non riuscito. Riprova.");
    } finally {
      setRetouchingExtra(false);
    }
  };

  const pendingAppointments = appointments.filter((a) => a.status !== "confermato").length;

  const fmtApptDate = (d) =>
    new Date(`${d}T12:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });

  const waPhone = (tel) => {
    let d = (tel || "").replace(/[^\d]/g, "");
    if (d.startsWith("00")) d = d.slice(2);
    // Un numero italiano completo di prefisso è di 12+ cifre (39 + 10); altrimenti anteponiamo 39
    if (!(d.startsWith("39") && d.length >= 12)) d = "39" + d;
    return d;
  };

  const waReminderUrl = (a) => {
    const reparto = a.ambito ? ` (${a.ambito === "lenti" ? "Lenti a Contatto" : "Occhiali da Vista"})` : "";
    const msg =
      `Ciao ${a.nome}, ti ricordiamo il tuo appuntamento da Stylottica Ceglie per ${fmtApptDate(a.data)} alle ${a.orario}${reparto}. ` +
      `Ti aspettiamo in Via Cristoforo Colombo 40, Ceglie Messapica. A presto! 👓`;
    return `https://wa.me/${waPhone(a.telefono)}?text=${encodeURIComponent(msg)}`;
  };

  // --- Vista appuntamenti: filtri, contatori, raggruppamento ---
  const todayStr = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD local
  const apptDT = (a) => new Date(`${a.data}T${(a.orario || "00:00")}:00`);
  const weekBounds = () => {
    const now = new Date();
    const day = (now.getDay() + 6) % 7; // 0 = lunedì
    const start = new Date(now); start.setDate(now.getDate() - day); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23, 59, 59, 999);
    return [start, end];
  };
  const [weekStart, weekEnd] = weekBounds();
  const apptCounters = {
    oggi: appointments.filter((a) => a.data === todayStr).length,
    settimana: appointments.filter((a) => { const d = apptDT(a); return d >= weekStart && d <= weekEnd; }).length,
    daConfermare: appointments.filter((a) => a.status !== "confermato").length,
  };
  const apptFiltered = appointments.filter((a) => {
    if (apptStatus !== "tutti" && (apptStatus === "confermato" ? a.status !== "confermato" : a.status === "confermato")) return false;
    if (apptReparto !== "tutti" && (a.ambito || "vista") !== apptReparto) return false;
    if (apptPeriod === "oggi" && a.data !== todayStr) return false;
    if (apptPeriod === "settimana") { const d = apptDT(a); if (d < weekStart || d > weekEnd) return false; }
    if (apptSearch.trim()) {
      const q = apptSearch.trim().toLowerCase();
      if (!(`${a.nome} ${a.telefono}`).toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const apptSorted = [...apptFiltered].sort((x, y) => {
    const xPast = x.data < todayStr, yPast = y.data < todayStr;
    if (xPast !== yPast) return xPast ? 1 : -1; // passati in fondo
    const cmp = apptDT(x) - apptDT(y);
    return xPast ? -cmp : cmp; // futuri crescente, passati decrescente
  });
  const apptDayLabel = (d) => {
    const dt = new Date(`${d}T12:00:00`);
    const t = new Date(`${todayStr}T12:00:00`);
    const diff = Math.round((dt - t) / 86400000);
    if (diff === 0) return "Oggi";
    if (diff === 1) return "Domani";
    if (diff === -1) return "Ieri";
    return dt.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  };
  const apptGroups = apptSorted.reduce((acc, a) => {
    (acc[a.data] = acc[a.data] || []).push(a);
    return acc;
  }, {});
  const apptGroupKeys = Object.keys(apptGroups); // già in ordine di inserimento (apptSorted)
  const repartoLabel = (a) => (a.ambito === "lenti" ? "Lenti a Contatto" : "Occhiali da Vista");

  const exportCSV = () => {
    const filtered = orders.filter((o) => {
      const day = String(o.created_at).slice(0, 10);
      if (exportFrom && day < exportFrom) return false;
      if (exportTo && day > exportTo) return false;
      return true;
    });
    if (!filtered.length) {
      toast.error("Nessun ordine nel periodo selezionato.");
      return;
    }
    const header = "Data;Cliente;Telefono;Articoli;Quantità;Totale EUR;Stato";
    const rows = filtered.map((o) =>
      [
        fmtDate(o.created_at),
        o.cliente_nome,
        o.cliente_telefono,
        o.items.map((it) => `${it.qty}x ${it.nome}`).join(" + "),
        o.items.reduce((s, it) => s + it.qty, 0),
        o.totale.toFixed(2).replace(".", ","),
        o.status === "completato" ? "Completato" : "In attesa",
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(";")
    );
    const csv = "\uFEFF" + [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    const periodo = exportFrom || exportTo ? `_${exportFrom || "inizio"}_${exportTo || "oggi"}` : "";
    a.download = `incassi-ottica-ceglie-${new Date().toISOString().slice(0, 10)}${periodo}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`CSV scaricato: ${filtered.length} ordini esportati.`);
  };

  useEffect(() => {
    if (user) {
      loadOrders();
      loadAppointments();
    }
  }, [user]);

  useEffect(() => {
    if (user && tab === "prodotti") {
      loadProducts();
      loadCollections();
    }
    if (user && tab === "promo") loadPromos();
    if (user && (tab === "ordini" || tab === "crm")) loadOrders();
    if (user && tab === "appuntamenti") loadAppointments();
    if (user && tab === "panoramica") loadDash();
  }, [user, tab]);

  const onCreateSubCollection = async (parentNome) => {
    const nome = (subInputs[parentNome] || "").trim();
    if (!nome) return;
    try {
      await axios.post(`${API}/admin/collections`, { nome, parent: parentNome }, { withCredentials: true });
      toast.success(`Sotto-collezione aggiunta a "${parentNome}".`);
      setSubInputs({ ...subInputs, [parentNome]: "" });
      loadCollections();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const resetProductForm = () => {
    setPForm({ nome: "", prezzo: "", categoria: "", sottocategoria: "", descrizione: "", stock: "", tryon: false, azione: "whatsapp", disponibile: true, in_evidenza: false, colore_lenti: "", taglia: "", misure: "" });
    setPFile(null);
    setPExtraFiles([]);
    setPKeepImages([]);
    setPRetouched(null);
    setPExtraRetouched([]);
    setPPhotos([]);
    setDragIdx(null);
    setPBgStyle("caldo");
    setPTexture("nessuna");
    setEditingId(null);
    if (fileRef.current) fileRef.current.value = "";
    if (extraFileRef.current) extraFileRef.current.value = "";
  };

  const onSubmitProduct = async (e) => {
    e.preventDefault();
    if (!editingId && !pFile && !pRetouched) {
      toast.error("Carica una foto della montatura.");
      return;
    }
    setPLoading(true);
    try {
      const fd = new FormData();
      fd.append("nome", pForm.nome);
      fd.append("prezzo", pForm.prezzo);
      fd.append("categoria", pForm.categoria);
      fd.append("descrizione", pForm.descrizione);
      if (pForm.sottocategoria) fd.append("sottocategoria", pForm.sottocategoria);
      if (pForm.stock !== "") fd.append("stock", pForm.stock);
      fd.append("tryon", pForm.tryon ? "true" : "false");
      fd.append("azione", pForm.azione || "whatsapp");
      fd.append("disponibile", pForm.disponibile === false ? "false" : "true");
      fd.append("in_evidenza", pForm.in_evidenza ? "true" : "false");
      fd.append("colore_lenti", pForm.colore_lenti || "");
      fd.append("taglia", pForm.taglia || "");
      fd.append("misure", pForm.misure || "");
      if (pFile) fd.append("file", pFile);
      else if (pRetouched) fd.append("image_path", pRetouched);
      if (editingId) {
        fd.append("photos_order", pPhotos.join(","));
      }
      pExtraFiles.forEach((f) => fd.append("extra_files", f));
      if (pExtraRetouched.length) fd.append("extra_paths", pExtraRetouched.join(","));
      if (editingId) {
        await axios.put(`${API}/admin/products/${editingId}`, fd, { withCredentials: true });
        toast.success("Prodotto aggiornato.");
      } else {
        await axios.post(`${API}/admin/products`, fd, { withCredentials: true });
        toast.success("Prodotto aggiunto alla vetrina.");
      }
      resetProductForm();
      loadProducts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPLoading(false);
    }
  };

  const onEditProduct = (p) => {
    setEditingId(p.id);
    setPForm({ nome: p.nome, prezzo: String(p.prezzo), categoria: p.categoria, sottocategoria: p.sottocategoria || "", descrizione: p.descrizione, stock: p.stock != null ? String(p.stock) : "", tryon: !!p.tryon, azione: p.azione || "whatsapp", disponibile: p.disponibile !== false, in_evidenza: !!p.in_evidenza, colore_lenti: p.colore_lenti || "", taglia: p.taglia || "", misure: p.misure || "" });
    setPFile(null);
    setPExtraFiles([]);
    setPKeepImages(p.images || []);
    setPRetouched(null);
    setPExtraRetouched([]);
    setPPhotos([p.image_path, ...(p.images || [])]);
    setDragIdx(null);
    if (fileRef.current) fileRef.current.value = "";
    if (extraFileRef.current) extraFileRef.current.value = "";
    document.querySelector('[data-testid="product-form"]')?.scrollIntoView({ behavior: "smooth" });
  };

  const onDeleteCollection = async (c) => {
    const count = products.filter((p) => p.categoria === c.nome || p.sottocategoria === c.nome).length;
    if (count > 0) {
      toast.error(`"${c.nome}" contiene ${count} prodott${count === 1 ? "o" : "i"}: spostali o eliminali prima.`);
      return;
    }
    try {
      await axios.delete(`${API}/admin/collections/${c.id}`, { withCredentials: true });
      toast.success("Collezione eliminata.");
      loadCollections();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onRenameCollection = async (c) => {
    const nome = renameValue.trim();
    if (!nome || nome === c.nome) {
      setRenamingId(null);
      return;
    }
    try {
      await axios.put(`${API}/admin/collections/${c.id}`, { nome }, { withCredentials: true });
      toast.success("Collezione rinominata: i prodotti sono stati aggiornati.");
      setRenamingId(null);
      loadCollections();
      loadProducts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onCreatePromo = async (e) => {
    e.preventDefault();
    setPromoLoading(true);
    try {
      const payload = {
        titolo: promoForm.titolo,
        tipo: promoForm.tipo,
        descrizione: promoForm.descrizione,
        valida_da: promoForm.valida_da,
        valida_fino: promoForm.valida_fino,
        percentuale: promoForm.tipo === "percentuale" ? Number(promoForm.percentuale) : 0,
        valore: promoForm.tipo === "fisso" || promoForm.tipo === "finale" ? Number(promoForm.valore) : 0,
        prezzo_originale: promoForm.tipo === "finale" && promoForm.prezzo_originale ? Number(promoForm.prezzo_originale) : null,
        testo_offerta: promoForm.tipo === "testo" ? promoForm.testo_offerta : "",
      };
      await axios.post(
        `${API}/admin/promos`,
        payload,
        { withCredentials: true }
      );
      toast.success("Promo creata e pubblicata sul sito.");
      setPromoForm({ titolo: "", tipo: "percentuale", percentuale: "", valore: "", prezzo_originale: "", testo_offerta: "", descrizione: "", valida_da: "", valida_fino: "" });
      loadPromos();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPromoLoading(false);
    }
  };

  const onGenDesc = async () => {
    setGenDesc(true);
    try {
      const r = await axios.post(
        `${API}/admin/ai-describe`,
        { nome: pForm.nome, categoria: pForm.categoria, sottocategoria: pForm.sottocategoria },
        { withCredentials: true, timeout: 60000 }
      );
      setPForm((f) => ({ ...f, descrizione: r.data.descrizione }));
      toast.success("Descrizione generata! Puoi modificarla prima di salvare.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generazione non riuscita. Riprova.");
    } finally {
      setGenDesc(false);
    }
  };

  const onDuplicate = (p) => {
    setPForm({
      nome: `${p.nome} (copia)`,
      prezzo: String(p.prezzo),
      categoria: p.categoria,
      sottocategoria: p.sottocategoria || "",
      descrizione: p.descrizione || "",
      stock: p.stock != null ? String(p.stock) : "",
      tryon: !!p.tryon,
      azione: p.azione || "whatsapp",
      disponibile: true,
      in_evidenza: false,
      colore_lenti: p.colore_lenti || "",
      taglia: p.taglia || "",
      misure: p.misure || "",
    });
    setEditingId(null);
    setPFile(null);
    setPRetouched(null);
    if (fileRef.current) fileRef.current.value = "";
    toast.success("Prodotto duplicato nel modulo: cambia nome e foto, poi salva.");
    document.querySelector('[data-testid="product-form"]')?.scrollIntoView({ behavior: "smooth" });
  };

  const promoBigLabel = (pr) => {
    const cur = (n) => Number(n).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
    if (pr.tipo === "fisso") return `-${cur(pr.valore)}`;
    if (pr.tipo === "finale") return `${pr.prezzo_originale ? cur(pr.prezzo_originale) + " → " : ""}${cur(pr.valore)}`;
    if (pr.tipo === "testo") return pr.testo_offerta;
    return `-${pr.percentuale}%`;
  };

  const onToggleAvailability = async (p) => {
    const next = p.disponibile === false;
    try {
      const fd = new FormData();
      fd.append("disponibile", next ? "true" : "false");
      await axios.patch(`${API}/admin/products/${p.id}/availability`, fd, { withCredentials: true });
      toast.success(next ? "Articolo segnato come disponibile." : "Articolo segnato come NON disponibile.");
      loadProducts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onToggleCollOnline = async (c) => {
    try {
      await axios.put(
        `${API}/admin/collections/${c.id}`,
        { nome: c.nome, parent: c.parent, online: c.online === false },
        { withCredentials: true }
      );
      toast.success(c.online === false ? `"${c.nome}" ora vendibile online.` : `"${c.nome}" ora solo in negozio.`);
      loadCollections();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento collezione");
    }
  };

  const onCollImage = async (c, f) => {
    const fd = new FormData();
    fd.append("file", f);
    try {
      await axios.post(`${API}/admin/collections/${c.id}/image`, fd, { withCredentials: true });
      toast.success(`Foto card di "${c.nome}" aggiornata.`);
      loadCollections();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload foto");
    }
  };

  const onRemoveCollImage = async (c) => {
    try {
      await axios.delete(`${API}/admin/collections/${c.id}/image`, { withCredentials: true });
      toast.success(`Foto card di "${c.nome}" rimossa.`);
      loadCollections();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore rimozione foto");
    }
  };

  const onTogglePromo = async (id) => {
    await axios.put(`${API}/admin/promos/${id}/toggle`, {}, { withCredentials: true });
    loadPromos();
  };

  const onDeletePromo = async (id) => {
    await axios.delete(`${API}/admin/promos/${id}`, { withCredentials: true });
    toast.success("Promo eliminata.");
    loadPromos();
  };

  const onDeleteProduct = async (id) => {
    await axios.delete(`${API}/admin/products/${id}`, { withCredentials: true });
    toast.success("Prodotto eliminato.");
    loadProducts();
  };

  const onLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
    setMessages([]);
    toast.success("Sessione terminata.");
  };

  if (user === null) {
    return <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center text-sm text-[#1A1B18]/50">Caricamento…</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6">
        <form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          className="w-full max-w-md border border-[#DCD7CC] bg-white/50 p-8 md:p-10"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#A86946] mb-2">Area Riservata</p>
          <h1 className="font-display text-3xl font-medium tracking-tighter">Stylottica Ceglie</h1>
          <p className="mt-2 text-sm text-[#1A1B18]/60">Accedi per leggere i messaggi ricevuti dal sito.</p>
          <div className="mt-8 space-y-5">
            <Input
              type="email"
              required
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input"
              className="rounded-none border-[#DCD7CC] bg-transparent h-12"
            />
            <Input
              type="password"
              required
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="rounded-none border-[#DCD7CC] bg-transparent h-12"
            />
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-700">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full rounded-full bg-[#A86946] text-white text-xs uppercase tracking-[0.15em] px-7 py-4 hover:bg-[#1A1B18] transition-colors duration-300 disabled:opacity-50"
            >
              {loading ? "Accesso…" : "Accedi"}
            </button>
            <a href="/" data-testid="admin-back-home" className="block text-center text-xs text-[#1A1B18]/50 hover:text-[#A86946] transition-colors">
              ← Torna al sito
            </a>
          </div>
        </form>
      </div>
    );
  }

  const NAV = [
    { id: "panoramica", label: "Panoramica", icon: LayoutDashboard },
    { id: "messaggi", label: "Messaggi", icon: Inbox },
    { id: "appuntamenti", label: "Appuntamenti", icon: CalendarCheck, badge: pendingAppointments },
    { id: "prodotti", label: "Prodotti", icon: Package },
    { id: "promo", label: "Promo", icon: Tag },
    { id: "ordini", label: "Ordini", icon: ClipboardList, badge: pendingOrders },
    { id: "crm", label: "CRM", icon: TrendingUp },
    { id: "contenuti", label: "Contenuti", icon: ImagePlus },
  ];

  const NavBtn = ({ item, after, prefix = "admin-tab" }) => (
    <button
      onClick={() => { setTab(item.id); after?.(); }}
      data-testid={`${prefix}-${item.id}`}
      className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
        tab === item.id ? "bg-[#A86946] text-white" : "text-white/60 hover:bg-white/10 hover:text-white"
      }`}
    >
      <item.icon size={17} strokeWidth={1.8} />
      <span className="flex-1 text-left">{item.label}</span>
      {item.badge > 0 && (
        <span
          data-testid={`${item.id}-pending-badge`}
          className="min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[#1A1B18] text-[10px] font-bold flex items-center justify-center"
        >
          {item.badge}
        </span>
      )}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <aside data-testid="admin-sidebar" className="hidden md:flex fixed inset-y-0 left-0 w-64 bg-[#1A1B18] text-white flex-col z-40">
        <div className="px-6 pt-8 pb-6 border-b border-white/10">
          <p className="font-display text-xl font-semibold leading-tight">Stylottica Ceglie</p>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#A86946] mt-1">Area Riservata</p>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1.5 overflow-y-auto">
          {NAV.map((item) => (
            <NavBtn key={item.id} item={item} />
          ))}
        </nav>
        <div className="px-3 py-5 border-t border-white/10">
          <button
            onClick={onLogout}
            data-testid="admin-logout-button"
            className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-xs uppercase tracking-[0.15em] text-white/60 hover:bg-white/10 hover:text-white transition-colors duration-300"
          >
            <LogOut size={17} /> Esci
          </button>
        </div>
      </aside>

      <div className="md:hidden sticky top-0 z-40 bg-[#1A1B18] text-white flex items-center justify-between px-5 h-14">
        <p className="font-display text-lg font-semibold">Stylottica Ceglie · <span className="text-[#A86946]">Admin</span></p>
        <button onClick={() => setMenuOpen(true)} data-testid="admin-menu-toggle" aria-label="Menu" className="p-2">
          <Menu size={22} />
        </button>
      </div>

      {menuOpen && (
        <div data-testid="admin-mobile-drawer" className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMenuOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#1A1B18] text-white flex flex-col">
            <div className="px-6 pt-6 pb-5 border-b border-white/10 flex items-center justify-between">
              <p className="font-display text-lg font-semibold">Area Riservata</p>
              <button onClick={() => setMenuOpen(false)} data-testid="admin-menu-close" aria-label="Chiudi" className="p-1">
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 px-3 py-5 space-y-1.5 overflow-y-auto">
              {NAV.map((item) => (
                <NavBtn key={item.id} item={item} after={() => setMenuOpen(false)} prefix="admin-tab-mobile" />
              ))}
            </nav>
            <div className="px-3 py-5 border-t border-white/10">
              <button
                onClick={onLogout}
                data-testid="admin-logout-mobile"
                className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-xs uppercase tracking-[0.15em] text-white/60 hover:bg-white/10 hover:text-white transition-colors duration-300"
              >
                <LogOut size={17} /> Esci
              </button>
            </div>
          </aside>
        </div>
      )}

      <main className="md:ml-64 px-5 md:px-10 py-8 md:py-12 max-w-6xl">

        {tab === "panoramica" && (
          <>
            <div className="flex items-baseline gap-4 mb-10">
              <LayoutDashboard size={22} className="text-[#A86946] translate-y-0.5" />
              <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tighter">Panoramica</h1>
            </div>
            {dash === "errore" ? (
              <div data-testid="dash-error" className="border border-[#DCD7CC] bg-white/50 p-10 text-center">
                <p className="text-sm text-[#1A1B18]/60 mb-4">Errore nel caricamento della panoramica.</p>
                <button onClick={loadDash} data-testid="dash-retry" className="rounded-full bg-[#A86946] text-white text-xs uppercase tracking-[0.15em] px-6 py-3 hover:bg-[#1A1B18] transition-colors">
                  Riprova
                </button>
              </div>
            ) : !dash ? (
              <p data-testid="dash-loading" className="text-sm text-[#1A1B18]/50">Caricamento…</p>
            ) : (
              <div className="space-y-6">
                <div data-testid="dash-stats" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {[
                    { label: "Appuntamenti oggi", val: dash.appts_oggi.length, icon: CalendarCheck, tid: "dash-appts-today" },
                    { label: "Da confermare", val: dash.appts_da_confermare.length, icon: AlertTriangle, tid: "dash-to-confirm" },
                    { label: "Ordini da evadere", val: dash.ordini_da_evadere.length, icon: ClipboardList, tid: "dash-orders-open" },
                    { label: "Messaggi ricevuti", val: dash.messaggi_totali, icon: Inbox, tid: "dash-msgs" },
                    { label: "Scorte in esaurimento", val: dash.scorte_basse.length, icon: Package, tid: "dash-low-stock" },
                    { label: "Clienti", val: dash.clienti_unici, icon: Users, tid: "dash-clients" },
                  ].map((c) => (
                    <div key={c.tid} data-testid={c.tid} className="border border-[#DCD7CC] bg-white/50 p-5">
                      <c.icon size={18} className="text-[#A86946] mb-3" />
                      <p className="font-display text-3xl font-medium">{c.val}</p>
                      <p className="text-[10px] uppercase tracking-[0.15em] text-[#1A1B18]/50 mt-1">{c.label}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="border border-[#DCD7CC] bg-white/50 p-6">
                    <h2 className="font-display text-xl font-medium mb-4">Oggi e domani</h2>
                    {dash.appts_oggi.length + dash.appts_domani.length === 0 ? (
                      <p className="text-sm text-[#1A1B18]/50">Nessun appuntamento in programma.</p>
                    ) : (
                      <ul className="space-y-3">
                        {[...dash.appts_oggi.map((a) => ({ ...a, when: "Oggi" })), ...dash.appts_domani.map((a) => ({ ...a, when: "Domani" }))].map((a, i) => (
                          <li key={a.id} data-testid={`dash-appt-${i}`} className="flex items-center justify-between gap-3 text-sm border-b border-[#DCD7CC] pb-3">
                            <div>
                              <p className="font-medium">{a.nome} · {a.when} ore {a.orario}</p>
                              <p className="text-[#1A1B18]/60">{a.servizio}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <a
                                href={waReminderUrl(a)}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`dash-reminder-${i}`}
                                title="Invia promemoria su WhatsApp al cliente"
                                className="flex items-center gap-1.5 rounded-full border border-[#25D366] text-[#128C3E] text-[10px] uppercase tracking-[0.12em] px-3 py-2 hover:bg-[#25D366] hover:text-white transition-colors"
                              >
                                <MessageCircle size={13} /> Promemoria
                              </a>
                              {a.status !== "confermato" && (
                                <button
                                  onClick={async () => {
                                    await axios.put(`${API}/admin/appointments/${a.id}/toggle`, {}, { withCredentials: true });
                                    loadDash();
                                  }}
                                  data-testid={`dash-confirm-${i}`}
                                  className="rounded-full bg-[#A86946] text-white text-[10px] uppercase tracking-[0.12em] px-4 py-2 hover:bg-[#1A1B18] transition-colors"
                                >
                                  Conferma
                                </button>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="border border-[#DCD7CC] bg-white/50 p-6">
                    <h2 className="font-display text-xl font-medium mb-4">Promemoria</h2>
                    {dash.appts_da_confermare.length === 0 && dash.scorte_basse.length === 0 ? (
                      <p className="text-sm text-[#1A1B18]/50">Tutto sotto controllo.</p>
                    ) : (
                      <ul className="space-y-3 text-sm">
                        {dash.appts_da_confermare.map((a) => (
                          <li key={`c-${a.id}`} className="flex gap-2">
                            <AlertTriangle size={15} className="text-[#A86946] shrink-0 mt-0.5" />
                            <span>Confermare: {a.nome}, {fmtApptDate(a.data)} ore {a.orario}</span>
                          </li>
                        ))}
                        {dash.scorte_basse.map((p) => (
                          <li key={`s-${p.id}`} className="flex gap-2">
                            <Package size={15} className="text-[#A86946] shrink-0 mt-0.5" />
                            <span>Scorta: {p.nome} ({p.stock === 0 ? "esaurito" : `ne resta ${p.stock}`})</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="border border-[#DCD7CC] bg-white/50 p-6">
                    <h2 className="font-display text-xl font-medium mb-4">Prodotti più ordinati</h2>
                    {dash.top_prodotti.length === 0 ? (
                      <p className="text-sm text-[#1A1B18]/50">Ancora nessun ordine.</p>
                    ) : (
                      <ul className="space-y-2 text-sm">
                        {dash.top_prodotti.map((p, i) => (
                          <li key={p.nome} data-testid={`dash-top-prod-${i}`} className="flex justify-between border-b border-[#DCD7CC] pb-2">
                            <span>{p.nome}</span>
                            <span className="font-semibold">{p.qty}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="border border-[#DCD7CC] bg-white/50 p-6">
                    <h2 className="font-display text-xl font-medium mb-4">Servizi più richiesti</h2>
                    {dash.top_servizi.length === 0 ? (
                      <p className="text-sm text-[#1A1B18]/50">Ancora nessun appuntamento.</p>
                    ) : (
                      <ul className="space-y-2 text-sm mb-8">
                        {dash.top_servizi.map((s, i) => (
                          <li key={s.servizio} data-testid={`dash-top-svc-${i}`} className="flex justify-between border-b border-[#DCD7CC] pb-2">
                            <span>{s.servizio}</span>
                            <span className="font-semibold">{s.n}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="border border-[#DCD7CC] bg-white/50 p-6">
                    <h2 className="font-display text-xl font-medium mb-4">Ordini per mese</h2>
                    {dash.ordini_per_mese.length === 0 ? (
                      <p className="text-sm text-[#1A1B18]/50">Ancora nessun ordine.</p>
                    ) : (
                      <div data-testid="dash-months" className="flex items-end gap-2 h-28">
                        {dash.ordini_per_mese.map((m) => {
                          const max = Math.max(...dash.ordini_per_mese.map((x) => x.n));
                          return (
                            <div key={m.mese} className="flex flex-col items-center justify-end gap-1 flex-1 h-full">
                              <span className="text-[10px] font-semibold">{m.n}</span>
                              <div className="w-full bg-[#A86946]/80 rounded-t" style={{ height: `${(m.n / max) * 70}%`, minHeight: 4 }} />
                              <span className="text-[9px] text-[#1A1B18]/50">{m.mese.slice(5)}/{m.mese.slice(2, 4)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {tab === "contenuti" && (
          <>
            <div className="flex items-baseline gap-4 mb-10">
              <ImagePlus size={22} className="text-[#A86946] translate-y-0.5" />
              <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tighter">Contenuti del sito</h1>
            </div>
            <ContenutiPanel />
          </>
        )}

        {tab === "messaggi" && (
          <>
            <div className="flex items-baseline gap-4 mb-10">
              <Inbox size={22} className="text-[#A86946] translate-y-0.5" />
              <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tighter">Messaggi ricevuti</h1>
              <span data-testid="admin-messages-count" className="text-sm text-[#1A1B18]/50">{messages.length}</span>
            </div>
            {messages.length === 0 ? (
              <p data-testid="admin-messages-empty" className="text-sm text-[#1A1B18]/50 border border-[#DCD7CC] p-10 text-center">
                Nessun messaggio per ora. Quando un cliente scrive dal sito, lo troverai qui.
              </p>
            ) : (
              <div data-testid="admin-messages-list" className="space-y-4">
                {messages.map((m, i) => (
                  <article key={m.id} data-testid={`message-card-${i}`} className="border border-[#DCD7CC] bg-white/50 p-6 md:p-8">
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                      <h2 className="font-display text-xl font-medium">{m.nome}</h2>
                      <span className="flex items-center gap-1.5 text-xs text-[#1A1B18]/50">
                        <CalendarDays size={14} /> {fmtDate(m.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-[#1A1B18]/80 leading-relaxed whitespace-pre-line">{m.messaggio}</p>
                    <div className="mt-4 pt-4 border-t border-[#DCD7CC] flex flex-wrap gap-x-8 gap-y-2 text-sm">
                      <a href={`mailto:${m.email}`} className="flex items-center gap-2 text-[#A86946] hover:text-[#1A1B18] transition-colors">
                        <Mail size={15} /> {m.email}
                      </a>
                      {m.telefono && (
                        <a href={`tel:${m.telefono}`} className="flex items-center gap-2 text-[#A86946] hover:text-[#1A1B18] transition-colors">
                          <Phone size={15} /> {m.telefono}
                        </a>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "appuntamenti" && (
          <>
            <div className="flex items-baseline gap-4 mb-10">
              <CalendarCheck size={22} className="text-[#A86946] translate-y-0.5" />
              <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tighter">Appuntamenti</h1>
              <span data-testid="admin-appt-count" className="text-sm text-[#1A1B18]/50">{appointments.length}</span>
            </div>
            <div data-testid="booking-links-card" className="mb-10 border border-[#DCD7CC] bg-white p-5 md:p-6">
              <div className="flex items-start gap-3">
                <Copy size={18} className="text-[#A86946] mt-0.5 shrink-0" />
                <div className="flex-1">
                  <h2 className="text-sm font-semibold">Link prenotazione da inviare ai clienti</h2>
                  <p className="text-xs text-[#1A1B18]/50 mt-1">Copia il link e invialo su WhatsApp, Instagram o email: aprirà il sito direttamente sul modulo di prenotazione.</p>
                  <div className="mt-4 space-y-2">
                    {BOOKING_LINKS.map((l) => (
                      <div key={l.key} className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs w-40 shrink-0 text-[#1A1B18]/70">{l.label}</span>
                        <code className="text-xs text-[#1A1B18]/60 bg-[#F4F1EA] px-2 py-1 rounded flex-1 min-w-[180px] truncate">{l.url}</code>
                        <button
                          onClick={() => { navigator.clipboard.writeText(l.url); toast.success("Link copiato negli appunti"); }}
                          data-testid={`copy-booking-${l.key}`}
                          className="flex items-center gap-1.5 rounded-full bg-[#A86946] text-white text-[10px] uppercase tracking-[0.12em] px-4 py-2 hover:bg-[#1A1B18] transition-colors shrink-0"
                        >
                          <Copy size={12} /> Copia
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div data-testid="appt-counters" className="grid grid-cols-3 gap-3 mb-6">
              {[
                { k: "oggi", label: "Oggi", val: apptCounters.oggi },
                { k: "settimana", label: "Questa settimana", val: apptCounters.settimana },
                { k: "daconfermare", label: "Da confermare", val: apptCounters.daConfermare },
              ].map((c) => (
                <div key={c.k} data-testid={`appt-counter-${c.k}`} className="border border-[#DCD7CC] bg-white p-4 text-center">
                  <p className="text-2xl md:text-3xl font-display font-medium text-[#A86946]">{c.val}</p>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#1A1B18]/50 mt-1">{c.label}</p>
                </div>
              ))}
            </div>
            <div className="mb-6 flex flex-wrap items-center gap-2 md:gap-3 admin-no-print">
              <div className="flex rounded-full border border-[#DCD7CC] overflow-hidden">
                {["tabella", "schede"].map((v) => (
                  <button key={v} onClick={() => setApptView(v)} data-testid={`appt-view-${v}`}
                    className={`px-4 py-2 text-[10px] uppercase tracking-[0.12em] transition-colors ${apptView === v ? "bg-[#1A1B18] text-white" : "text-[#1A1B18]/60 hover:bg-[#F4F1EA]"}`}>
                    {v === "tabella" ? "Tabella" : "Schede"}
                  </button>
                ))}
              </div>
              <input value={apptSearch} onChange={(e) => setApptSearch(e.target.value)} placeholder="Cerca nome o telefono…" data-testid="appt-search"
                className="flex-1 min-w-[160px] rounded-full border border-[#DCD7CC] bg-white h-10 px-4 text-sm focus:outline-none focus:border-[#A86946]" />
              <select value={apptStatus} onChange={(e) => setApptStatus(e.target.value)} data-testid="appt-filter-status" className="rounded-full border border-[#DCD7CC] bg-white h-10 px-3 text-sm cursor-pointer">
                <option value="tutti">Tutti gli stati</option>
                <option value="richiesto">Da confermare</option>
                <option value="confermato">Confermati</option>
              </select>
              <select value={apptReparto} onChange={(e) => setApptReparto(e.target.value)} data-testid="appt-filter-reparto" className="rounded-full border border-[#DCD7CC] bg-white h-10 px-3 text-sm cursor-pointer">
                <option value="tutti">Tutti i reparti</option>
                <option value="vista">Occhiali da Vista</option>
                <option value="lenti">Lenti a Contatto</option>
              </select>
              <select value={apptPeriod} onChange={(e) => setApptPeriod(e.target.value)} data-testid="appt-filter-period" className="rounded-full border border-[#DCD7CC] bg-white h-10 px-3 text-sm cursor-pointer">
                <option value="tutti">Tutti i periodi</option>
                <option value="oggi">Oggi</option>
                <option value="settimana">Questa settimana</option>
              </select>
              <button onClick={() => window.print()} data-testid="appt-print" className="flex items-center gap-2 rounded-full border border-[#1A1B18]/30 h-10 px-4 text-[10px] uppercase tracking-[0.12em] hover:bg-[#1A1B18] hover:text-white transition-colors">
                <Download size={13} /> Stampa
              </button>
              <button onClick={() => setShowApptForm((v) => !v)} data-testid="appt-add-toggle" className="flex items-center gap-2 rounded-full bg-[#A86946] text-white h-10 px-4 text-[10px] uppercase tracking-[0.12em] hover:bg-[#1A1B18] transition-colors">
                <CalendarDays size={13} /> {showApptForm ? "Chiudi" : "Nuovo appuntamento"}
              </button>
            </div>
            {showApptForm && (
              <form onSubmit={onCreateAppointment} data-testid="appt-add-form" className="mb-6 border border-[#A86946]/40 bg-[#A86946]/5 p-5 admin-no-print">
                <p className="text-sm font-medium text-[#1A1B18] mb-1">Aggiungi appuntamento manuale</p>
                <p className="text-xs text-[#1A1B18]/50 mb-4">Orario libero: puoi inserire qualsiasi ora, anche fuori dagli slot standard.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input required value={apptForm.nome} onChange={(e) => setApptForm({ ...apptForm, nome: e.target.value })} placeholder="Nome cliente *" data-testid="appt-input-nome" className="rounded-lg border border-[#DCD7CC] bg-white h-10 px-3 text-sm focus:outline-none focus:border-[#A86946]" />
                  <input value={apptForm.telefono} onChange={(e) => setApptForm({ ...apptForm, telefono: e.target.value })} placeholder="Telefono" data-testid="appt-input-telefono" className="rounded-lg border border-[#DCD7CC] bg-white h-10 px-3 text-sm focus:outline-none focus:border-[#A86946]" />
                  <input required type="date" value={apptForm.data} onChange={(e) => setApptForm({ ...apptForm, data: e.target.value })} data-testid="appt-input-data" className="rounded-lg border border-[#DCD7CC] bg-white h-10 px-3 text-sm focus:outline-none focus:border-[#A86946]" />
                  <input required type="time" value={apptForm.orario} onChange={(e) => setApptForm({ ...apptForm, orario: e.target.value })} data-testid="appt-input-orario" className="rounded-lg border border-[#DCD7CC] bg-white h-10 px-3 text-sm focus:outline-none focus:border-[#A86946]" />
                  <select value={apptForm.ambito} onChange={(e) => setApptForm({ ...apptForm, ambito: e.target.value })} data-testid="appt-input-ambito" className="rounded-lg border border-[#DCD7CC] bg-white h-10 px-3 text-sm cursor-pointer">
                    <option value="vista">Occhiali da Vista</option>
                    <option value="lenti">Lenti a Contatto</option>
                  </select>
                  <input value={apptForm.servizio} onChange={(e) => setApptForm({ ...apptForm, servizio: e.target.value })} placeholder="Servizio (es. Controllo vista)" data-testid="appt-input-servizio" className="rounded-lg border border-[#DCD7CC] bg-white h-10 px-3 text-sm focus:outline-none focus:border-[#A86946]" />
                  <input value={apptForm.note} onChange={(e) => setApptForm({ ...apptForm, note: e.target.value })} placeholder="Note (facoltative)" data-testid="appt-input-note" className="sm:col-span-2 rounded-lg border border-[#DCD7CC] bg-white h-10 px-3 text-sm focus:outline-none focus:border-[#A86946]" />
                </div>
                <div className="mt-4 flex gap-3">
                  <button type="submit" disabled={savingAppt} data-testid="appt-save" className="rounded-full bg-[#1A1B18] text-white text-xs uppercase tracking-[0.12em] px-6 py-2.5 hover:bg-[#A86946] transition-colors disabled:opacity-40">
                    {savingAppt ? "Salvataggio…" : "Salva appuntamento"}
                  </button>
                  <button type="button" onClick={() => { setShowApptForm(false); setApptForm(emptyApptForm); }} className="rounded-full border border-[#1A1B18]/30 text-xs uppercase tracking-[0.12em] px-6 py-2.5 hover:bg-[#1A1B18] hover:text-white transition-colors">
                    Annulla
                  </button>
                </div>
              </form>
            )}
            {appointments.length === 0 ? (
              <p data-testid="admin-appt-empty" className="text-sm text-[#1A1B18]/50 border border-[#DCD7CC] p-10 text-center">
                Nessun appuntamento per ora. Quando un cliente prenota una visita dal sito, la troverai qui.
              </p>
            ) : apptView === "tabella" ? (
              <div data-testid="admin-appt-table-wrap" className="overflow-x-auto border border-[#DCD7CC] bg-white">
                {apptSorted.length === 0 ? (
                  <p className="text-sm text-[#1A1B18]/50 p-10 text-center">Nessun appuntamento con questi filtri.</p>
                ) : (
                  <table data-testid="admin-appt-table" className="w-full text-sm border-collapse min-w-[820px]">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[#1A1B18]/50 border-b border-[#DCD7CC]">
                        <th className="py-3 px-4">Data / Ora</th>
                        <th className="py-3 px-4">Cliente</th>
                        <th className="py-3 px-4">Telefono</th>
                        <th className="py-3 px-4">Reparto</th>
                        <th className="py-3 px-4">Servizio</th>
                        <th className="py-3 px-4">Stato</th>
                        <th className="py-3 px-4 text-right admin-no-print">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apptGroupKeys.flatMap((day) => [
                        <tr key={`h-${day}`} className="bg-[#F4F1EA]">
                          <td colSpan={7} className="py-2 px-4 text-[11px] uppercase tracking-[0.15em] font-semibold text-[#A86946]">
                            {apptDayLabel(day)} · {apptGroups[day].length}
                          </td>
                        </tr>,
                        ...apptGroups[day].map((a) => (
                          <tr key={a.id} data-testid={`appt-row-${a.id}`} className={`border-b border-[#EFEBE2] align-top ${a.status === "confermato" ? "bg-[#F7FBF4]" : ""}`}>
                            <td className="py-3 px-4 font-semibold whitespace-nowrap">{a.orario}</td>
                            <td className="py-3 px-4">{a.nome}</td>
                            <td className="py-3 px-4"><a href={`tel:${a.telefono}`} className="text-[#A86946] hover:underline whitespace-nowrap">{a.telefono}</a></td>
                            <td className="py-3 px-4"><span className="text-[10px] uppercase tracking-[0.1em] px-2 py-1 rounded-full bg-[#E8E4DA] text-[#A86946] font-semibold whitespace-nowrap">{repartoLabel(a)}</span></td>
                            <td className="py-3 px-4 text-[#1A1B18]/70">{a.servizio}{a.note ? <span className="block text-xs text-[#1A1B18]/40 italic mt-0.5">Note: {a.note}</span> : null}</td>
                            <td className="py-3 px-4"><span className={`text-[10px] uppercase tracking-[0.1em] px-2.5 py-1 rounded-full whitespace-nowrap ${a.status === "confermato" ? "bg-[#4A5240] text-white" : "bg-[#A86946] text-white"}`}>{a.status === "confermato" ? "Confermato" : "Da confermare"}</span></td>
                            <td className="py-3 px-4 admin-no-print">
                              <div className="flex items-center justify-end gap-1.5 flex-wrap">
                                <a href={waReminderUrl(a)} target="_blank" rel="noopener noreferrer" data-testid={`appt-row-reminder-${a.id}`} title="Promemoria WhatsApp" className="p-2 rounded-full border border-[#25D366] text-[#128C3E] hover:bg-[#25D366] hover:text-white transition-colors"><MessageCircle size={14} /></a>
                                <button onClick={() => onToggleAppointment(a.id)} data-testid={`appt-row-toggle-${a.id}`} title={a.status === "confermato" ? "Riapri" : "Conferma"} className="p-2 rounded-full border border-[#1A1B18]/30 hover:bg-[#1A1B18] hover:text-white transition-colors"><CheckCircle2 size={14} /></button>
                                <button onClick={() => onDeleteAppointment(a.id)} data-testid={`appt-row-delete-${a.id}`} title="Elimina" className="p-2 rounded-full border border-red-800/30 text-red-800 hover:bg-red-800 hover:text-white transition-colors"><Trash2 size={14} /></button>
                              </div>
                            </td>
                          </tr>
                        )),
                      ])}
                    </tbody>
                  </table>
                )}
              </div>
            ) : apptSorted.length === 0 ? (
              <p className="text-sm text-[#1A1B18]/50 border border-[#DCD7CC] p-10 text-center">Nessun appuntamento con questi filtri.</p>
            ) : (
              <div data-testid="admin-appt-list" className="space-y-4">
                {apptSorted.map((a, i) => (
                  <article key={a.id} data-testid={`appt-card-${i}`} className={`border border-[#DCD7CC] bg-white/50 p-6 md:p-8 ${a.status === "confermato" ? "opacity-60" : ""}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      <div className="flex items-center gap-4">
                        <h2 className="font-display text-xl font-medium">{a.nome}</h2>
                        <span
                          data-testid={`appt-status-${i}`}
                          className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full ${
                            a.status === "confermato" ? "bg-[#4A5240] text-white" : "bg-[#A86946] text-white"
                          }`}
                        >
                          {a.status === "confermato" ? "Confermato" : "Da confermare"}
                        </span>
                      </div>
                      <span className="flex items-center gap-1.5 text-xs text-[#1A1B18]/50">
                        <CalendarDays size={14} /> Richiesto il {fmtDate(a.created_at)}
                      </span>
                    </div>
                    <div className="text-sm text-[#1A1B18]/80 space-y-1">
                      <p data-testid={`appt-when-${i}`} className="font-semibold">
                        {fmtApptDate(a.data)} · ore {a.orario}
                      </p>
                      {a.ambito && (
                        <span
                          data-testid={`appt-ambito-${i}`}
                          className="inline-block text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full bg-[#E8E4DA] text-[#A86946] font-semibold"
                        >
                          {a.ambito === "lenti" ? "Lenti a Contatto" : "Occhiali da Vista"}
                        </span>
                      )}
                      <p className="text-[#1A1B18]/60">{a.servizio}</p>
                    </div>
                    {a.note && <p className="text-sm text-[#1A1B18]/60 mt-3 italic">Note: {a.note}</p>}
                    <div className="mt-4 pt-4 border-t border-[#DCD7CC] flex flex-wrap items-center gap-x-8 gap-y-2">
                      <a href={`tel:${a.telefono}`} className="flex items-center gap-2 text-sm text-[#A86946] hover:text-[#1A1B18] transition-colors">
                        <Phone size={15} /> {a.telefono}
                      </a>
                      <a
                        href={waReminderUrl(a)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`appt-reminder-${i}`}
                        title="Invia promemoria su WhatsApp al cliente"
                        className="flex items-center gap-2 rounded-full border border-[#25D366] text-[#128C3E] text-xs uppercase tracking-[0.15em] px-5 py-2.5 hover:bg-[#25D366] hover:text-white transition-colors duration-300"
                      >
                        <MessageCircle size={14} /> Promemoria
                      </a>
                      <button
                        onClick={() => onToggleAppointment(a.id)}
                        data-testid={`appt-toggle-${i}`}
                        className="ml-auto flex items-center gap-2 rounded-full border border-[#1A1B18]/30 text-xs uppercase tracking-[0.15em] px-5 py-2.5 hover:bg-[#1A1B18] hover:text-white transition-colors duration-300"
                      >
                        <CheckCircle2 size={14} />
                        {a.status === "confermato" ? "Riapri" : "Conferma"}
                      </button>
                      <button
                        onClick={() => onDeleteAppointment(a.id)}
                        data-testid={`appt-delete-${i}`}
                        className="flex items-center gap-2 rounded-full border border-red-800/30 text-red-800 text-xs uppercase tracking-[0.15em] px-5 py-2.5 hover:bg-red-800 hover:text-white transition-colors duration-300"
                      >
                        <Trash2 size={14} /> Elimina
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "prodotti" && (
          <>
            <div className="flex items-baseline gap-4 mb-10">
              <Package size={22} className="text-[#A86946] translate-y-0.5" />
              <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tighter">Catalogo montature</h1>
              <span data-testid="admin-products-count" className="text-sm text-[#1A1B18]/50">{products.length}</span>
            </div>
            <form
              onSubmit={onSubmitProduct}
              data-testid="product-form"
              className="border border-[#DCD7CC] bg-white/50 p-6 md:p-8 mb-10"
            >
              <h2 className="font-display text-xl font-medium mb-6">
                {editingId ? "Modifica montatura" : "Aggiungi una montatura"}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Input
                  required
                  placeholder="Nome modello (es. Police Teen Nero)"
                  value={pForm.nome}
                  onChange={(e) => setPForm({ ...pForm, nome: e.target.value })}
                  data-testid="product-input-nome"
                  className="rounded-none border-[#DCD7CC] bg-transparent h-12"
                />
                <Input
                  required
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Prezzo in € (es. 89.00)"
                  value={pForm.prezzo}
                  onChange={(e) => setPForm({ ...pForm, prezzo: e.target.value })}
                  data-testid="product-input-prezzo"
                  className="rounded-none border-[#DCD7CC] bg-transparent h-12"
                />
                <div className="md:col-span-2">
                  <label className="block text-xs text-[#1A1B18]/50 mb-1.5">1 · Scegli collezione e sotto-collezione</label>
                  <div className="flex flex-wrap items-center gap-3">
                    <select
                      required
                      value={pForm.categoria}
                      onChange={(e) => setPForm({ ...pForm, categoria: e.target.value, sottocategoria: "" })}
                      data-testid="product-input-categoria"
                      className="rounded-none border border-[#DCD7CC] bg-transparent h-12 px-3 text-sm min-w-[200px]"
                    >
                      <option value="" disabled>Collezione…</option>
                      {collections.filter((c) => !c.parent).map((c) => (
                        <option key={c.id} value={c.nome}>{c.nome}</option>
                      ))}
                    </select>
                    {pForm.categoria && collections.some((c) => c.parent === pForm.categoria) && (
                      <select
                        value={pForm.sottocategoria}
                        onChange={(e) => setPForm({ ...pForm, sottocategoria: e.target.value })}
                        data-testid="product-input-sottocategoria"
                        className="rounded-none border border-[#DCD7CC] bg-transparent h-12 px-3 text-sm min-w-[200px]"
                      >
                        <option value="">Sotto-collezione (facoltativa)…</option>
                        {collections.filter((c) => c.parent === pForm.categoria).map((c) => (
                          <option key={c.id} value={c.nome}>{c.nome}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Pezzi in stock (facoltativo)"
                  value={pForm.stock}
                  onChange={(e) => setPForm({ ...pForm, stock: e.target.value })}
                  data-testid="product-input-stock"
                  className="rounded-none border-[#DCD7CC] bg-transparent h-12"
                />
                <label className="flex items-center gap-3 text-sm cursor-pointer select-none border border-[#DCD7CC] px-4 h-12">
                  <input
                    type="checkbox"
                    checked={pForm.tryon}
                    onChange={(e) => setPForm({ ...pForm, tryon: e.target.checked })}
                    data-testid="product-tryon-toggle"
                    className="w-4 h-4 accent-[#A86946]"
                  />
                  <span className="flex items-center gap-2"><ScanFace size={15} className="text-[#A86946]" /> Prova virtuale attiva</span>
                </label>
                <label className="flex items-center gap-3 text-sm cursor-pointer select-none border border-[#DCD7CC] px-4 h-12">
                  <input
                    type="checkbox"
                    checked={pForm.in_evidenza}
                    onChange={(e) => {
                      const on = e.target.checked;
                      if (on) {
                        const cnt = products.filter((p) => p.in_evidenza && p.id !== editingId).length;
                        if (cnt >= 3) { toast.error("Massimo 3 prodotti in evidenza in home"); return; }
                      }
                      setPForm({ ...pForm, in_evidenza: on });
                    }}
                    data-testid="product-evidenza-toggle"
                    className="w-4 h-4 accent-[#A86946]"
                  />
                  <span className="flex items-center gap-2"><Star size={15} className="text-[#A86946]" /> In evidenza in home (max 3)</span>
                </label>
                <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-[#1A1B18]/50 mb-1.5">Colore lenti</label>
                    <select
                      value={pForm.colore_lenti}
                      onChange={(e) => setPForm({ ...pForm, colore_lenti: e.target.value })}
                      data-testid="product-input-colore"
                      className="w-full rounded-none border border-[#DCD7CC] bg-transparent h-12 px-3 text-sm cursor-pointer"
                    >
                      <option value="">— nessuno —</option>
                      {LENS_COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#1A1B18]/50 mb-1.5">Grandezza (vestibilità)</label>
                    <select
                      value={pForm.taglia}
                      onChange={(e) => setPForm({ ...pForm, taglia: e.target.value })}
                      data-testid="product-input-taglia"
                      className="w-full rounded-none border border-[#DCD7CC] bg-transparent h-12 px-3 text-sm cursor-pointer"
                    >
                      <option value="">— nessuna —</option>
                      {["Piccola", "Media", "Grande"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#1A1B18]/50 mb-1.5">Misure mm (calibro-ponte-asta)</label>
                    <Input
                      value={pForm.misure}
                      onChange={(e) => setPForm({ ...pForm, misure: e.target.value })}
                      placeholder="es. 52-18-145"
                      data-testid="product-input-misure"
                      className="rounded-none border-[#DCD7CC] bg-transparent h-12"
                    />
                  </div>
                </div>
                <div className="md:col-span-2 border border-[#DCD7CC] p-4">
                  <label className="block text-xs text-[#1A1B18]/50 mb-3">Azione principale sulla card in vetrina</label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={() => setPForm({ ...pForm, azione: "whatsapp" })}
                      data-testid="product-azione-whatsapp"
                      className={`flex-1 flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors duration-300 ${
                        pForm.azione !== "visita" ? "border-[#A86946] bg-[#A86946]/10" : "border-[#DCD7CC] hover:border-[#A86946]"
                      }`}
                    >
                      <ShoppingBag size={18} className="text-[#A86946] shrink-0" />
                      <span>
                        <span className="block text-sm font-medium">Ordina / Chiedi su WhatsApp</span>
                        <span className="block text-[11px] text-[#1A1B18]/50">Aggiungi al carrello + chiedi disponibilità</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPForm({ ...pForm, azione: "visita" })}
                      data-testid="product-azione-visita"
                      className={`flex-1 flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors duration-300 ${
                        pForm.azione === "visita" ? "border-[#A86946] bg-[#A86946]/10" : "border-[#DCD7CC] hover:border-[#A86946]"
                      }`}
                    >
                      <CalendarCheck size={18} className="text-[#A86946] shrink-0" />
                      <span>
                        <span className="block text-sm font-medium">Prenota una visita</span>
                        <span className="block text-[11px] text-[#1A1B18]/50">La card invita a prenotare in negozio</span>
                      </span>
                    </button>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs text-[#1A1B18]/50">2 · Descrizione</label>
                    <button
                      type="button"
                      onClick={onGenDesc}
                      disabled={genDesc || !pForm.nome.trim()}
                      data-testid="product-ai-describe"
                      className="flex items-center gap-2 rounded-full border border-[#A86946] text-[#A86946] text-[10px] uppercase tracking-[0.12em] px-4 py-2 hover:bg-[#A86946] hover:text-white transition-colors duration-300 disabled:opacity-40"
                    >
                      <Sparkles size={12} />
                      {genDesc ? "Sto scrivendo…" : "Genera descrizione con AI"}
                    </button>
                  </div>
                  <textarea
                    placeholder="Descrizione (facoltativa) — oppure lasciala scrivere all'AI dal nome del modello"
                    value={pForm.descrizione}
                    onChange={(e) => setPForm({ ...pForm, descrizione: e.target.value })}
                    data-testid="product-input-descrizione"
                    className="w-full rounded-none border border-[#DCD7CC] bg-transparent p-3 text-sm min-h-[80px] focus:outline-none focus:border-[#A86946]"
                  />
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  required={!editingId && !pRetouched}
                  onChange={(e) => setPFile(e.target.files[0] || null)}
                  data-testid="product-input-foto"
                  className="text-sm file:mr-4 file:rounded-full file:border-0 file:bg-[#A86946] file:text-white file:px-4 file:py-2 file:text-xs file:uppercase file:tracking-[0.1em] hover:file:bg-[#1A1B18] file:transition-colors"
                />
                {editingId && (
                  <p className="text-xs text-[#1A1B18]/50 self-center">Lascia vuoto per mantenere la foto attuale</p>
                )}
                <div className="sm:col-span-2 border-t border-[#DCD7CC] pt-4">
                  <label className="block text-xs text-[#1A1B18]/50 mb-2">Foto aggiuntive (facoltative) — il cliente le sfoglia con le frecce sulla card</label>
                  <input
                    ref={extraFileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => setPExtraFiles(Array.from(e.target.files || []))}
                    data-testid="product-input-foto-extra"
                    className="text-sm file:mr-4 file:rounded-full file:border-0 file:bg-[#1A1B18] file:text-white file:px-4 file:py-2 file:text-xs file:uppercase file:tracking-[0.1em] hover:file:bg-[#A86946] file:transition-colors"
                  />
                  {pExtraFiles.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <p data-testid="product-extra-count" className="text-xs text-[#A86946]">{pExtraFiles.length} nuov{pExtraFiles.length === 1 ? "a foto pronta" : "e foto pronte"} da caricare</p>
                      <button
                        type="button"
                        onClick={onRetouchExtra}
                        disabled={retouchingExtra}
                        data-testid="product-retouch-extra-ai"
                        className="flex items-center gap-2 rounded-full border border-[#A86946] text-[#A86946] text-[10px] uppercase tracking-[0.12em] px-4 py-2 hover:bg-[#A86946] hover:text-white transition-colors duration-300 disabled:opacity-40"
                      >
                        <Sparkles size={12} />
                        {retouchingExtra ? "Ritocco in corso…" : "Ritocca aggiuntive con AI"}
                      </button>
                      <span className="text-[11px] text-[#1A1B18]/45">La foto principale non viene toccata.</span>
                    </div>
                  )}
                  {pExtraRetouched.length > 0 && (
                    <div data-testid="product-extra-retouched-list" className="mt-3">
                      <p className="text-[11px] text-[#A86946] mb-2">Foto aggiuntive ritoccate (verranno salvate):</p>
                      <div className="flex flex-wrap gap-3">
                        {pExtraRetouched.map((img, k) => (
                          <div key={img} className="relative">
                            <img src={`${API}/files/${img}`} alt={`Extra ritoccata ${k + 1}`} className="w-16 h-16 object-cover border border-[#A86946]" />
                            <button
                              type="button"
                              onClick={() => setPExtraRetouched(pExtraRetouched.filter((_, idx) => idx !== k))}
                              data-testid={`product-extra-retouched-remove-${k}`}
                              aria-label="Rimuovi foto ritoccata"
                              className="absolute -top-2 -right-2 rounded-full bg-red-700 text-white w-5 h-5 text-xs leading-none"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {editingId && pPhotos.length > 0 && (
                    <div className="mt-4">
                      <label className="block text-xs text-[#1A1B18]/50 mb-2">Ordine foto — trascina per riordinare. La <strong>prima</strong> è la copertina mostrata per prima in vetrina.</label>
                      <div data-testid="product-photos-order" className="flex flex-wrap gap-3">
                        {pPhotos.map((img, k) => (
                          <div
                            key={img}
                            draggable
                            onDragStart={() => setDragIdx(k)}
                            onDragEnd={() => setDragIdx(null)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => {
                              if (dragIdx === null || dragIdx === k) return;
                              const arr = [...pPhotos];
                              const [m] = arr.splice(dragIdx, 1);
                              arr.splice(k, 0, m);
                              setPPhotos(arr);
                              setDragIdx(null);
                            }}
                            data-testid={`product-photo-${k}`}
                            className={`relative cursor-grab active:cursor-grabbing transition-opacity ${dragIdx === k ? "opacity-40" : ""}`}
                          >
                            <img src={`${API}/files/${img}`} alt={`Foto ${k + 1}`} className={`w-20 h-20 object-cover ${k === 0 ? "border-2 border-[#A86946]" : "border border-[#DCD7CC]"}`} />
                            {k === 0 && <span className="absolute bottom-0 inset-x-0 bg-[#A86946] text-white text-[8px] text-center uppercase tracking-wide py-0.5">Copertina</span>}
                            <button
                              type="button"
                              onClick={() => setPPhotos(pPhotos.filter((_, idx) => idx !== k))}
                              data-testid={`product-photo-remove-${k}`}
                              aria-label="Rimuovi foto"
                              className="absolute -top-2 -right-2 rounded-full bg-red-700 text-white w-5 h-5 text-xs leading-none"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="sm:col-span-2 border-t border-[#DCD7CC] pt-4">
                  <label className="block text-xs text-[#1A1B18]/50 mb-2">Direzione sfondo (per il ritocco AI)</label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      ["caldo", "Caldo boutique", "linear-gradient(135deg,#F1E7D7,#C7A079)"],
                      ["bianco", "Bianco studio", "linear-gradient(135deg,#ffffff,#ececec)"],
                      ["grigio", "Grigio moderno", "linear-gradient(135deg,#dedede,#b0b0b0)"],
                      ["nero", "Nero elegante", "linear-gradient(135deg,#3a3a3a,#0d0d0d)"],
                      ["verde", "Verde mediterraneo", "linear-gradient(135deg,#dbe4c6,#93a86f)"],
                      ["blu", "Blu notte", "linear-gradient(135deg,#3a5580,#132038)"],
                    ].map(([val, label, sw]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setPBgStyle(val)}
                        data-testid={`bg-style-${val}`}
                        className={`flex flex-col items-center gap-1 rounded-lg p-1.5 border transition-all ${pBgStyle === val ? "border-[#A86946] ring-2 ring-[#A86946]/30 bg-[#A86946]/5" : "border-[#DCD7CC] hover:border-[#A86946]"}`}
                      >
                        <span className="block w-16 h-11 rounded-md border border-black/5 shadow-inner" style={{ backgroundImage: sw }} />
                        <span className="text-[10px] text-[#1A1B18]/75 leading-tight">{label}</span>
                      </button>
                    ))}
                  </div>
                  <label className="block text-xs text-[#1A1B18]/50 mb-2 mt-4">Superficie / texture</label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      ["nessuna", "Nessuna", "#EDE9E1"],
                      ["legno", "Legno", "repeating-linear-gradient(88deg,#b07c45,#b07c45 4px,#9a672f 4px,#9a672f 8px)"],
                      ["marmo", "Marmo", "linear-gradient(120deg,#f4f4f2 58%,#c4c4c4 60%,#f4f4f2 63%,#f4f4f2 78%,#cfcfcf 80%,#f4f4f2 82%)"],
                      ["pietra", "Pietra", "radial-gradient(circle at 30% 30%,#d3ccc1,#a79e90)"],
                      ["lino", "Lino", "repeating-linear-gradient(45deg,#e9dfd1,#e9dfd1 3px,#dccdb9 3px,#dccdb9 6px)"],
                    ].map(([val, label, sw]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setPTexture(val)}
                        data-testid={`texture-${val}`}
                        className={`flex flex-col items-center gap-1 rounded-lg p-1.5 border transition-all ${pTexture === val ? "border-[#1A1B18] ring-2 ring-[#1A1B18]/20 bg-[#1A1B18]/5" : "border-[#DCD7CC] hover:border-[#1A1B18]"}`}
                      >
                        <span className="block w-16 h-11 rounded-md border border-black/5 shadow-inner" style={{ background: sw }} />
                        <span className="text-[10px] text-[#1A1B18]/75 leading-tight">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
                  <button
                    type="button"
                    onClick={onRetouchProduct}
                    disabled={retouchingP}
                    data-testid="product-retouch-ai"
                    className="flex items-center gap-2 rounded-full border border-[#A86946] text-[#A86946] text-xs uppercase tracking-[0.15em] px-6 py-3 hover:bg-[#A86946] hover:text-white transition-colors duration-300 disabled:opacity-40"
                  >
                    <Sparkles size={14} />
                    {retouchingP ? "Ritocco in corso… (può richiedere un minuto)" : "Ritocca con AI"}
                  </button>
                  <span className="text-xs text-[#1A1B18]/50 max-w-sm">
                    L'AI mantiene la montatura esattamente com'è e uniforma sfondo e luce allo stile del sito.
                  </span>
                  {pRetouched && (
                    <img
                      src={`${API}/files/${pRetouched}`}
                      alt="Anteprima ritocco AI"
                      data-testid="product-retouched-preview"
                      className="w-20 h-20 object-cover border border-[#A86946]"
                    />
                  )}
                </div>
                <div className="md:col-span-2 border-t border-[#DCD7CC] pt-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#1A1B18]/50 mb-3">Anteprima live · come apparirà in vetrina</p>
                  <div data-testid="product-live-preview" className="max-w-xs">
                    <div className="overflow-hidden bg-[#E8E4DA]/50 aspect-[4/3]">
                      {pRetouched || pFile || editingId ? (
                        <img
                          src={
                            pRetouched
                              ? `${API}/files/${pRetouched}`
                              : pFile
                                ? URL.createObjectURL(pFile)
                                : `${API}/files/${products.find((x) => x.id === editingId)?.image_path}`
                          }
                          alt="Anteprima prodotto"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs text-[#1A1B18]/30">Nessuna foto ancora</div>
                      )}
                    </div>
                    <div className="mt-3 flex items-baseline justify-between gap-2">
                      <h3 className="font-display text-lg font-medium">{pForm.nome || "Nome modello"}</h3>
                      <span className="text-sm font-semibold text-[#A86946]">
                        {pForm.prezzo ? Number(pForm.prezzo).toLocaleString("it-IT", { style: "currency", currency: "EUR" }) : "€ —"}
                      </span>
                    </div>
                    <p className="text-xs uppercase tracking-[0.15em] text-[#1A1B18]/50 mt-1">
                      {pForm.categoria || "Collezione"}
                      {pForm.sottocategoria && <span className="text-[#A86946]"> · {pForm.sottocategoria}</span>}
                      {pForm.stock !== "" && <span> · Stock: {pForm.stock} pz</span>}
                    </p>
                    {(pForm.colore_lenti || pForm.taglia || pForm.misure) && (
                      <p data-testid="preview-specs" className="text-[11px] text-[#1A1B18]/55 mt-1.5">
                        {[pForm.colore_lenti && `Lenti ${pForm.colore_lenti}`, pForm.taglia && `Taglia ${pForm.taglia}`, pForm.misure].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    {pForm.descrizione && <p className="text-sm text-[#1A1B18]/60 mt-2">{pForm.descrizione}</p>}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {pForm.in_evidenza && (
                        <span data-testid="preview-evidenza-badge" className="inline-flex items-center gap-1.5 rounded-full bg-[#A86946]/10 text-[#A86946] text-[10px] uppercase tracking-[0.15em] px-4 py-2">
                          <Star size={12} /> In evidenza
                        </span>
                      )}
                      {pForm.azione === "visita" ? (
                        <span data-testid="preview-cta-visita" className="inline-flex items-center gap-2 rounded-full bg-[#A86946] text-white text-[10px] uppercase tracking-[0.15em] px-4 py-2">
                          <CalendarCheck size={12} /> Prenota visita
                        </span>
                      ) : (
                        <span data-testid="preview-cta-whatsapp" className="inline-flex items-center gap-2 rounded-full bg-[#1A1B18] text-white text-[10px] uppercase tracking-[0.15em] px-4 py-2">Aggiungi al carrello</span>
                      )}
                      {pForm.tryon && (
                        <span data-testid="preview-tryon-badge" className="inline-flex items-center gap-1.5 rounded-full border border-[#A86946] text-[#A86946] text-[10px] uppercase tracking-[0.15em] px-4 py-2">
                          <ScanFace size={12} /> Prova virtuale
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="submit"
                  disabled={pLoading}
                  data-testid="product-submit"
                  className="rounded-full bg-[#A86946] text-white text-xs uppercase tracking-[0.15em] px-7 py-3.5 hover:bg-[#1A1B18] transition-colors duration-300 disabled:opacity-50"
                >
                  {pLoading ? "Salvataggio…" : editingId ? "Salva modifiche" : "Aggiungi prodotto"}
                </button>
                {editingId && (
                  <button
                    type="button"
                    onClick={resetProductForm}
                    data-testid="product-cancel-edit"
                    className="rounded-full border border-[#1A1B18]/30 text-xs uppercase tracking-[0.15em] px-7 py-3.5 hover:border-[#1A1B18] transition-colors duration-300"
                  >
                    Annulla
                  </button>
                )}
              </div>
            </form>
            <div data-testid="collections-panel" className="border border-[#DCD7CC] bg-white/50 mb-10">
              <input
                ref={collFileRef}
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="collection-image-input"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f && collImgTarget) onCollImage(collImgTarget, f);
                  e.target.value = "";
                }}
              />
              <h2 className="font-display text-xl font-medium px-6 md:px-8 py-5 border-b border-[#DCD7CC]">
                Le tue collezioni
              </h2>
              {collections.filter((c) => !c.parent).map((c, i) => {
                const subs = collections.filter((s) => s.parent === c.nome);
                const count = products.filter((p) => p.categoria === c.nome).length;
                return (
                  <div key={c.id} data-testid={`collection-row-${i}`} className="border-b border-[#DCD7CC] last:border-b-0">
                    <div className="flex flex-wrap items-center justify-between gap-4 px-6 md:px-8 py-4">
                      {renamingId === c.id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            data-testid={`collection-rename-input-${i}`}
                            className="rounded-none border-[#DCD7CC] bg-transparent h-10 w-56"
                          />
                          <button onClick={() => onRenameCollection(c)} data-testid={`collection-rename-save-${i}`} className="rounded-full bg-[#1A1B18] text-white text-xs uppercase tracking-[0.1em] px-4 py-2.5 hover:bg-[#A86946] transition-colors">Salva</button>
                          <button onClick={() => setRenamingId(null)} data-testid={`collection-rename-cancel-${i}`} className="text-xs text-[#1A1B18]/50 hover:text-[#1A1B18]">Annulla</button>
                        </div>
                      ) : (
                        <span className="font-display text-lg font-medium flex items-center gap-3">
                          {c.image_path && (
                            <span className="relative inline-block">
                              <img src={`${API}/files/${c.image_path}`} alt={`Card ${c.nome}`} className="w-10 h-10 object-cover border border-[#DCD7CC]" />
                              <button
                                onClick={() => onRemoveCollImage(c)}
                                data-testid={`collection-image-remove-${i}`}
                                aria-label="Rimuovi foto card"
                                className="absolute -top-1.5 -right-1.5 rounded-full bg-red-700 text-white w-4 h-4 text-[10px] leading-none"
                              >
                                ×
                              </button>
                            </span>
                          )}
                          {c.nome}
                        </span>
                      )}
                      <div className="flex items-center gap-5">
                        <span data-testid={`collection-count-${i}`} className="text-xs uppercase tracking-[0.15em] text-[#1A1B18]/50">
                          {count} prodott{count === 1 ? "o" : "i"}
                        </span>
                        <button
                          onClick={() => onToggleCollOnline(c)}
                          data-testid={`collection-online-${i}`}
                          title="Se attiva, i prodotti di questa collezione si possono comprare online; se no, appare l'invito a prenotare in negozio"
                          className={`text-xs uppercase tracking-[0.15em] transition-colors ${c.online !== false ? "text-green-700 hover:text-green-800" : "text-[#1A1B18]/50 hover:text-[#1A1B18]"}`}
                        >
                          {c.online !== false ? "● Vendibile online" : "○ Solo in negozio"}
                        </button>
                        <button
                          onClick={() => { setCollImgTarget(c); collFileRef.current?.click(); }}
                          data-testid={`collection-image-${i}`}
                          className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#A86946] hover:text-[#1A1B18] transition-colors"
                        >
                          <ImagePlus size={14} /> Foto card
                        </button>
                        {renamingId !== c.id && (
                          <button
                            onClick={() => { setRenamingId(c.id); setRenameValue(c.nome); }}
                            data-testid={`collection-rename-${i}`}
                            className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#A86946] hover:text-[#1A1B18] transition-colors"
                          >
                            <Pencil size={14} /> Rinomina
                          </button>
                        )}
                        <button
                          onClick={() => onDeleteCollection(c)}
                          data-testid={`collection-delete-${i}`}
                          className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-red-700/70 hover:text-red-700 transition-colors"
                        >
                          <Trash2 size={14} /> Elimina
                        </button>
                      </div>
                    </div>
                    {subs.map((s, j) => (
                      <div key={s.id} data-testid={`sub-collection-row-${i}-${j}`} className="flex flex-wrap items-center justify-between gap-4 pl-10 md:pl-14 pr-6 md:pr-8 py-3 border-t border-[#DCD7CC]/60 bg-[#F0EDE5]/40">
                        {renamingId === s.id ? (
                          <div className="flex items-center gap-2 flex-1">
                            <Input
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              data-testid={`sub-collection-rename-input-${i}-${j}`}
                              className="rounded-none border-[#DCD7CC] bg-transparent h-10 w-56"
                            />
                            <button onClick={() => onRenameCollection(s)} data-testid={`sub-collection-rename-save-${i}-${j}`} className="rounded-full bg-[#1A1B18] text-white text-xs uppercase tracking-[0.1em] px-4 py-2.5 hover:bg-[#A86946] transition-colors">Salva</button>
                            <button onClick={() => setRenamingId(null)} className="text-xs text-[#1A1B18]/50 hover:text-[#1A1B18]">Annulla</button>
                          </div>
                        ) : (
                          <span className="text-sm text-[#1A1B18]/80">↳ {s.nome}</span>
                        )}
                        <div className="flex items-center gap-5">
                          <span className="text-xs uppercase tracking-[0.15em] text-[#1A1B18]/50">
                            {products.filter((p) => p.sottocategoria === s.nome).length} prodotti
                          </span>
                          {renamingId !== s.id && (
                            <button
                              onClick={() => { setRenamingId(s.id); setRenameValue(s.nome); }}
                              data-testid={`sub-collection-rename-${i}-${j}`}
                              className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#A86946] hover:text-[#1A1B18] transition-colors"
                            >
                              <Pencil size={13} /> Rinomina
                            </button>
                          )}
                          <button
                            onClick={() => onDeleteCollection(s)}
                            data-testid={`sub-collection-delete-${i}-${j}`}
                            className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-red-700/70 hover:text-red-700 transition-colors"
                          >
                            <Trash2 size={13} /> Elimina
                          </button>
                        </div>
                      </div>
                    ))}
                    <div className="flex flex-wrap items-center gap-2 pl-10 md:pl-14 pr-6 md:pr-8 py-3 border-t border-[#DCD7CC]/60">
                      <Input
                        placeholder={`Nuova sotto-collezione di "${c.nome}"`}
                        value={subInputs[c.nome] || ""}
                        onChange={(e) => setSubInputs({ ...subInputs, [c.nome]: e.target.value })}
                        data-testid={`sub-collection-input-${i}`}
                        className="rounded-none border-[#DCD7CC] bg-transparent h-10 w-64"
                      />
                      <button
                        type="button"
                        onClick={() => onCreateSubCollection(c.nome)}
                        data-testid={`sub-collection-create-${i}`}
                        className="rounded-full bg-[#A86946] text-white text-xs uppercase tracking-[0.1em] px-4 py-2.5 hover:bg-[#1A1B18] transition-colors"
                      >
                        + Aggiungi
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {products.length === 0 ? (
              <p data-testid="admin-products-empty" className="text-sm text-[#1A1B18]/50 border border-[#DCD7CC] p-10 text-center">
                Catalogo vuoto. Aggiungi la prima montatura con il modulo qui sopra.
              </p>
            ) : (
              <div data-testid="admin-products-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {products.map((p, i) => (
                  <article key={p.id} data-testid={`product-card-${i}`} className={`relative border overflow-hidden group ${p.disponibile === false ? "border-[#1A1B18]/20 bg-[#1A1B18]/5" : p.stock != null && p.stock < 2 ? "border-red-400 bg-red-50/50" : "border-[#DCD7CC] bg-white/50"}`}>
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={`${API}/files/${p.image_path}`}
                        alt={p.nome}
                        className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${p.disponibile === false ? "grayscale opacity-60" : ""}`}
                      />
                      {p.disponibile === false && (
                        <span data-testid={`product-unavailable-badge-${i}`} className="absolute top-3 left-3 rounded-full bg-[#1A1B18] text-white text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5">
                          Non disponibile
                        </span>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-baseline justify-between gap-2">
                        <h3 className="font-display text-lg font-medium">{p.nome}</h3>
                        <span className="text-sm font-semibold text-[#A86946]">
                          {p.prezzo.toLocaleString("it-IT", { style: "currency", currency: "EUR" })}
                        </span>
                      </div>
                      <p className="text-xs uppercase tracking-[0.15em] text-[#1A1B18]/50 mt-1">
                        {p.categoria}
                        {p.sottocategoria && <span className="text-[#A86946]"> · {p.sottocategoria}</span>}
                        {p.stock != null && (
                          <span className={p.stock < 2 ? "text-red-700 font-bold" : ""}>
                            {" "}· Stock: {p.stock} pz{p.stock < 2 && " · DA RIASSORTIRE"}
                          </span>
                        )}
                        {p.tryon && <span className="text-[#A86946] font-semibold"> · Prova virtuale</span>}
                      </p>
                      {p.descrizione && <p className="text-sm text-[#1A1B18]/60 mt-2">{p.descrizione}</p>}
                      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
                        <button
                          onClick={() => onEditProduct(p)}
                          data-testid={`product-edit-${i}`}
                          className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#A86946] hover:text-[#1A1B18] transition-colors"
                        >
                          <Pencil size={14} /> Modifica
                        </button>
                        <button
                          onClick={() => onToggleAvailability(p)}
                          data-testid={`product-availability-toggle-${i}`}
                          className={`flex items-center gap-2 text-xs uppercase tracking-[0.15em] transition-colors ${p.disponibile === false ? "text-green-700/80 hover:text-green-700" : "text-[#1A1B18]/60 hover:text-[#1A1B18]"}`}
                        >
                          {p.disponibile === false ? (<><CheckCircle2 size={14} /> Segna disponibile</>) : (<><AlertTriangle size={14} /> Non disponibile</>)}
                        </button>
                        <button
                          onClick={() => onDuplicate(p)}
                          data-testid={`product-duplicate-${i}`}
                          className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#1A1B18]/60 hover:text-[#1A1B18] transition-colors"
                        >
                          <Copy size={14} /> Duplica
                        </button>
                        <button
                          onClick={() => onDeleteProduct(p.id)}
                          data-testid={`product-delete-${i}`}
                          className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-red-700/70 hover:text-red-700 transition-colors"
                        >
                          <Trash2 size={14} /> Elimina
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        )}
        {tab === "promo" && (
          <>
            <div className="flex items-baseline gap-4 mb-10">
              <Tag size={22} className="text-[#A86946] translate-y-0.5" />
              <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tighter">Promo</h1>
              <span data-testid="admin-promos-count" className="text-sm text-[#1A1B18]/50">{promos.length}</span>
              <p className="text-xs text-[#1A1B18]/45 ml-auto max-w-[280px] text-right">In home viene mostrata solo la promo attiva più recente, nel suo periodo di validità</p>
            </div>
            <form
              onSubmit={onCreatePromo}
              data-testid="promo-form"
              className="border border-[#DCD7CC] bg-white/50 p-6 md:p-8 mb-10"
            >
              <h2 className="font-display text-xl font-medium mb-6">Crea una promo</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Input
                  required
                  placeholder="Titolo (es. Rottamazione vecchi occhiali)"
                  value={promoForm.titolo}
                  onChange={(e) => setPromoForm({ ...promoForm, titolo: e.target.value })}
                  data-testid="promo-input-titolo"
                  className="rounded-none border-[#DCD7CC] bg-transparent h-12"
                />
                <div>
                  <label className="block text-xs text-[#1A1B18]/50 mb-1.5">Tipo di sconto</label>
                  <select
                    value={promoForm.tipo}
                    onChange={(e) => setPromoForm({ ...promoForm, tipo: e.target.value })}
                    data-testid="promo-input-tipo"
                    className="w-full rounded-none border border-[#DCD7CC] bg-transparent h-12 px-3 text-sm focus:outline-none focus:border-[#A86946]"
                  >
                    <option value="percentuale">Percentuale (es. -20%)</option>
                    <option value="fisso">Importo fisso (es. -30€)</option>
                    <option value="finale">Prezzo finale diretto (es. da 159€ a 119€)</option>
                    <option value="testo">Offerta libera (es. "Secondo paio -50%")</option>
                  </select>
                </div>
                {promoForm.tipo === "percentuale" && (
                  <Input
                    required
                    type="number"
                    min="1"
                    max="90"
                    placeholder="Sconto % (es. 20)"
                    value={promoForm.percentuale}
                    onChange={(e) => setPromoForm({ ...promoForm, percentuale: e.target.value })}
                    data-testid="promo-input-percentuale"
                    className="rounded-none border-[#DCD7CC] bg-transparent h-12"
                  />
                )}
                {(promoForm.tipo === "fisso" || promoForm.tipo === "finale") && (
                  <Input
                    required
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder={promoForm.tipo === "fisso" ? "Sconto in € (es. 30)" : "Prezzo scontato in € (es. 119)"}
                    value={promoForm.valore}
                    onChange={(e) => setPromoForm({ ...promoForm, valore: e.target.value })}
                    data-testid="promo-input-valore"
                    className="rounded-none border-[#DCD7CC] bg-transparent h-12"
                  />
                )}
                {promoForm.tipo === "finale" && (
                  <Input
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="Prezzo originale barrato in € (es. 159)"
                    value={promoForm.prezzo_originale}
                    onChange={(e) => setPromoForm({ ...promoForm, prezzo_originale: e.target.value })}
                    data-testid="promo-input-prezzo-originale"
                    className="rounded-none border-[#DCD7CC] bg-transparent h-12"
                  />
                )}
                {promoForm.tipo === "testo" && (
                  <Input
                    required
                    placeholder='Testo offerta (es. "Secondo paio -50%")'
                    value={promoForm.testo_offerta}
                    onChange={(e) => setPromoForm({ ...promoForm, testo_offerta: e.target.value })}
                    data-testid="promo-input-testo"
                    className="rounded-none border-[#DCD7CC] bg-transparent h-12"
                  />
                )}
                <Input
                  placeholder="Descrizione (facoltativa)"
                  value={promoForm.descrizione}
                  onChange={(e) => setPromoForm({ ...promoForm, descrizione: e.target.value })}
                  data-testid="promo-input-descrizione"
                  className="rounded-none border-[#DCD7CC] bg-transparent h-12"
                />
                <div>
                  <label className="block text-xs text-[#1A1B18]/50 mb-1.5">Valida dal (facoltativo — lascia vuoto per pubblicarla subito)</label>
                  <Input
                    type="date"
                    value={promoForm.valida_da}
                    onChange={(e) => setPromoForm({ ...promoForm, valida_da: e.target.value })}
                    data-testid="promo-input-inizio"
                    className="rounded-none border-[#DCD7CC] bg-transparent h-12"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#1A1B18]/50 mb-1.5">Valida fino al (facoltativo — scade da sola a mezzanotte)</label>
                  <Input
                    type="date"
                    value={promoForm.valida_fino}
                    onChange={(e) => setPromoForm({ ...promoForm, valida_fino: e.target.value })}
                    data-testid="promo-input-scadenza"
                    className="rounded-none border-[#DCD7CC] bg-transparent h-12"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={promoLoading}
                data-testid="promo-submit"
                className="mt-6 rounded-full bg-[#A86946] text-white text-xs uppercase tracking-[0.15em] px-7 py-3.5 hover:bg-[#1A1B18] transition-colors duration-300 disabled:opacity-50"
              >
                {promoLoading ? "Creazione…" : "Crea promo"}
              </button>
            </form>
            {promos.length === 0 ? (
              <p data-testid="admin-promos-empty" className="text-sm text-[#1A1B18]/50 border border-[#DCD7CC] p-10 text-center">
                Nessuna promo attiva. Creane una con il modulo qui sopra: apparirà subito sul sito.
              </p>
            ) : (
              <div data-testid="admin-promos-list" className="space-y-4">
                {promos.map((pr, i) => (
                  <article key={pr.id} data-testid={`promo-card-${i}`} className={`border border-[#DCD7CC] bg-white/50 p-6 flex flex-wrap items-center gap-6 ${!pr.attiva ? "opacity-50" : ""}`}>
                    <span data-testid={`promo-value-${i}`} className="font-display text-3xl font-semibold text-[#A86946] max-w-[280px]">{promoBigLabel(pr)}</span>
                    <div className="flex-1 min-w-[200px]">
                      <h3 className="font-display text-xl font-medium">
                        {pr.titolo}
                        {!pr.attiva && <span className="ml-3 text-xs uppercase tracking-[0.15em] text-[#1A1B18]/40">disattivata</span>}
                      </h3>
                      {pr.descrizione && <p className="text-sm text-[#1A1B18]/60 mt-1">{pr.descrizione}</p>}
                      {(pr.valida_da || pr.valida_fino) && (
                        <p className="text-xs text-[#1A1B18]/50 mt-1">
                          {pr.valida_da && `Dal ${new Date(pr.valida_da + "T12:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}`}
                          {pr.valida_da && pr.valida_fino && " · "}
                          {pr.valida_fino && `Fino al ${new Date(pr.valida_fino + "T12:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}`}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-4">
                      <button
                        onClick={() => onTogglePromo(pr.id)}
                        data-testid={`promo-toggle-${i}`}
                        className="text-xs uppercase tracking-[0.15em] text-[#A86946] hover:text-[#1A1B18] transition-colors"
                      >
                        {pr.attiva ? "Disattiva" : "Attiva"}
                      </button>
                      <button
                        onClick={() => onDeletePromo(pr.id)}
                        data-testid={`promo-delete-${i}`}
                        className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-red-700/70 hover:text-red-700 transition-colors"
                      >
                        <Trash2 size={14} /> Elimina
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "ordini" && (
          <>
            <div className="flex items-baseline gap-4 mb-10">
              <ClipboardList size={22} className="text-[#A86946] translate-y-0.5" />
              <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tighter">Ordini</h1>
              <span data-testid="admin-orders-count" className="text-sm text-[#1A1B18]/50">{orders.length}</span>
            </div>
            {orders.length === 0 ? (
              <p data-testid="admin-orders-empty" className="text-sm text-[#1A1B18]/50 border border-[#DCD7CC] p-10 text-center">
                Nessun ordine per ora. Quando un cliente ordina dalla vetrina, lo troverai qui.
              </p>
            ) : (
              <div data-testid="admin-orders-list" className="space-y-4">
                {orders.map((o, i) => (
                  <article key={o.id} data-testid={`order-card-${i}`} className={`border border-[#DCD7CC] bg-white/50 p-6 md:p-8 ${o.status === "completato" ? "opacity-60" : ""}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      <div className="flex items-center gap-4">
                        <h2 className="font-display text-xl font-medium">{o.cliente_nome}</h2>
                        <span
                          data-testid={`order-status-${i}`}
                          className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full ${
                            o.status === "completato" ? "bg-[#4A5240] text-white" : "bg-[#A86946] text-white"
                          }`}
                        >
                          {o.status === "completato" ? "Completato" : "Da completare"}
                        </span>
                      </div>
                      <span className="flex items-center gap-1.5 text-xs text-[#1A1B18]/50">
                        <CalendarDays size={14} /> {fmtDate(o.created_at)}
                      </span>
                    </div>
                    <ul className="text-sm text-[#1A1B18]/80 space-y-1">
                      {o.items.map((it, j) => (
                        <li key={j}>
                          {it.qty}× {it.nome} — <span className="font-semibold">{eur(it.prezzo * it.qty)}</span>
                        </li>
                      ))}
                    </ul>
                    {o.note && <p className="text-sm text-[#1A1B18]/60 mt-3 italic">Note: {o.note}</p>}
                    <div className="mt-4 pt-4 border-t border-[#DCD7CC] flex flex-wrap items-center gap-x-8 gap-y-2">
                      <span className="font-display text-xl font-semibold text-[#A86946]" data-testid={`order-totale-${i}`}>{eur(o.totale)}</span>
                      <a href={`tel:${o.cliente_telefono}`} className="flex items-center gap-2 text-sm text-[#A86946] hover:text-[#1A1B18] transition-colors">
                        <Phone size={15} /> {o.cliente_telefono}
                      </a>
                      <button
                        onClick={() => onToggleOrder(o.id)}
                        data-testid={`order-toggle-${i}`}
                        className="ml-auto flex items-center gap-2 rounded-full border border-[#1A1B18]/30 text-xs uppercase tracking-[0.15em] px-5 py-2.5 hover:bg-[#1A1B18] hover:text-white transition-colors duration-300"
                      >
                        <CheckCircle2 size={14} />
                        {o.status === "completato" ? "Riapri" : "Segna completato"}
                      </button>
                      <button
                        onClick={() => onDeleteOrder(o.id)}
                        data-testid={`order-delete-${i}`}
                        className="flex items-center gap-2 rounded-full border border-red-800/30 text-red-800 text-xs uppercase tracking-[0.15em] px-5 py-2.5 hover:bg-red-800 hover:text-white transition-colors duration-300"
                      >
                        <Trash2 size={14} /> Elimina
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "crm" && (() => {
          const completati = orders.filter((o) => o.status === "completato");
          const inAttesa = orders.filter((o) => o.status !== "completato");
          const incassato = completati.reduce((s, o) => s + o.totale, 0);
          const valoreAttesa = inAttesa.reduce((s, o) => s + o.totale, 0);
          const stats = [
            { id: "incassato", label: "Incassato (ordini completati)", value: eur(incassato) },
            { id: "attesa", label: "Valore ordini in attesa", value: eur(valoreAttesa) },
            { id: "completati", label: "Ordini completati", value: String(completati.length) },
            { id: "totali", label: "Ordini totali", value: String(orders.length) },
          ];
          return (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
                <div className="flex items-baseline gap-4">
                  <TrendingUp size={22} className="text-[#A86946] translate-y-0.5" />
                  <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tighter">CRM · Entrate</h1>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-[#1A1B18]/50 mb-1">Da</label>
                    <Input
                      type="date"
                      value={exportFrom}
                      onChange={(e) => setExportFrom(e.target.value)}
                      data-testid="crm-export-from"
                      className="rounded-none border-[#DCD7CC] bg-transparent h-10 w-40 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-[#1A1B18]/50 mb-1">A</label>
                    <Input
                      type="date"
                      value={exportTo}
                      onChange={(e) => setExportTo(e.target.value)}
                      data-testid="crm-export-to"
                      className="rounded-none border-[#DCD7CC] bg-transparent h-10 w-40 text-sm"
                    />
                  </div>
                  <button
                    onClick={exportCSV}
                    disabled={orders.length === 0}
                    data-testid="crm-export-csv"
                    className="flex items-center gap-2 rounded-full bg-[#1A1B18] text-white text-xs uppercase tracking-[0.15em] px-5 py-2.5 hover:bg-[#A86946] transition-colors duration-300 disabled:opacity-40"
                  >
                    <Download size={14} /> Scarica CSV
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#DCD7CC] border border-[#DCD7CC] mb-10">
                {stats.map((s, i) => (
                  <div key={s.id} data-testid={`crm-stat-${s.id}`} className="bg-[#FAF9F6] p-6">
                    <p className="font-display text-2xl md:text-3xl font-medium text-[#A86946]">{s.value}</p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-[#1A1B18]/50">{s.label}</p>
                  </div>
                ))}
              </div>
              {orders.length === 0 ? (
                <p data-testid="crm-empty" className="text-sm text-[#1A1B18]/50 border border-[#DCD7CC] p-10 text-center">
                  Nessun movimento ancora. Gli ordini della vetrina appariranno qui.
                </p>
              ) : (
                <div data-testid="crm-table" className="border border-[#DCD7CC]">
                  <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#DCD7CC] text-[10px] uppercase tracking-[0.15em] text-[#1A1B18]/50">
                    <span className="col-span-3">Data</span>
                    <span className="col-span-3">Cliente</span>
                    <span className="col-span-3">Articoli</span>
                    <span className="col-span-2">Totale</span>
                    <span className="col-span-1 text-right">Stato</span>
                  </div>
                  {orders.map((o, i) => (
                    <div key={o.id} data-testid={`crm-row-${i}`} className="grid grid-cols-2 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 border-b border-[#DCD7CC] last:border-b-0 text-sm items-center">
                      <span className="col-span-3 text-[#1A1B18]/60">{fmtDate(o.created_at)}</span>
                      <span className="col-span-3 font-medium">{o.cliente_nome}</span>
                      <span className="col-span-3 text-[#1A1B18]/60">{o.items.reduce((s, it) => s + it.qty, 0)} articol{o.items.reduce((s, it) => s + it.qty, 0) === 1 ? "o" : "i"}</span>
                      <span className="col-span-2 font-semibold text-[#A86946]">{eur(o.totale)}</span>
                      <span className={`col-span-1 md:text-right text-[10px] uppercase tracking-[0.1em] ${o.status === "completato" ? "text-[#4A5240]" : "text-[#A86946]"}`}>
                        {o.status === "completato" ? "Fatto" : "Attesa"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          );
        })()}
      </main>
    </div>
  );
}
